import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGame, type CraftResult } from '../state/store'
import { Decimal, fmt } from '../engine/decimal'
import { GLOBAL } from '../config/global'
import { dailyWord } from '../config/dictionary'

interface FlyingLetter {
  id: number
  char: string
  startX: number
  startY: number
}

function feedback(craft: CraftResult) {
  switch (craft.status) {
    case 'invalid':
      return { text: `"${craft.word}" is not in the dictionary`, cls: 'text-rarity-rare' }
    case 'locked':
      return { text: `You don't own every letter in ${craft.word} yet`, cls: 'text-ink-muted' }
    case 'nocharge':
      return {
        text: `${craft.word} collected — no Quill Charge, so no Ink burst`,
        cls: 'text-ink-muted',
      }
    case 'burst':
      return {
        text: `${craft.word} → +${fmt(new Decimal(craft.burst ?? '0'))} Ink${
          craft.chain > 1 ? `  ·  ×${craft.chain} chain` : ''
        }${craft.isNew ? '  ·  NEW!' : ''}`,
        cls: 'text-ink-teal',
      }
  }
}

function burstTier(burst: string): number {
  const val = parseFloat(burst)
  if (val > 1000) return 4
  if (val > 100) return 3
  if (val > 10) return 2
  return 1
}

export function WordCraft() {
  const [input, setInput] = useState('')
  const [flyingLetters, setFlyingLetters] = useState<FlyingLetter[]>([])
  const [nochargeShake, setNochargeShake] = useState(false)
  const craftWord = useGame((s) => s.craftWord)
  const quillCharge = useGame((s) => s.quillCharge)
  const quillRegen = useGame((s) => s.quillRegen)
  const chainLength = useGame((s) => s.chainLength)
  const lastCraft = useGame((s) => s.lastCraft)
  const inputRef = useRef<HTMLInputElement>(null)
  const nextId = useRef(0)

  // Daily word — recompute on day rollover (not module load)
  const [todayKey, setTodayKey] = useState(() => new Date().toDateString())
  const daily = useMemo(() => dailyWord(), [todayKey])

  // Check for day change every minute
  useEffect(() => {
    const id = setInterval(() => {
      const now = new Date().toDateString()
      if (now !== todayKey) setTodayKey(now)
    }, 60_000)
    return () => clearInterval(id)
  }, [todayKey])

  const submit = useCallback(() => {
    if (!input.trim()) return

    // Flying letters
    const inputEl = inputRef.current
    if (inputEl) {
      const rect = inputEl.getBoundingClientRect()
      const letters: FlyingLetter[] = input.toUpperCase().split('').map((char, i) => ({
        id: nextId.current++,
        char,
        startX: rect.left + (rect.width * i) / input.length,
        startY: rect.top,
      }))
      setFlyingLetters(letters)
      setTimeout(() => setFlyingLetters([]), 600)
    }

    craftWord(input)
    setInput('')

    // Nocharge shake
    if (quillCharge <= 0) {
      setNochargeShake(true)
      setTimeout(() => setNochargeShake(false), 400)
    }
  }, [input, craftWord, quillCharge])

  const fb = lastCraft ? feedback(lastCraft) : null
  const regenPct = Math.min(100, (quillRegen / GLOBAL.quillChargeRegenSeconds) * 100)
  const tier = lastCraft?.burst ? burstTier(lastCraft.burst) : 0

  return (
    <section className="relative rounded-lg border border-ink-border bg-ink-panel p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xs font-bold uppercase tracking-widest text-ink-teal">
          Wordsmith
        </h2>
        <div className="flex items-center gap-1" title="Quill Charges — spend 1 per Ink burst">
          {Array.from({ length: GLOBAL.quillChargeMax }).map((_, i) => (
            <motion.span
              key={i}
              animate={i < quillCharge ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className={`h-3 w-3 rounded-full border ${
                i < quillCharge
                  ? 'border-ink-teal bg-ink-teal shadow-glow'
                  : 'border-ink-border bg-ink-bg'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="spell a word…"
          spellCheck={false}
          autoComplete="off"
          className="flex-1 rounded-md border border-ink-border bg-ink-bg px-3 py-2 text-sm uppercase tracking-wider text-ink-text outline-none placeholder:normal-case placeholder:text-ink-muted focus:border-ink-tealdim"
        />
        <motion.button
          whileTap={{ scale: 0.95 }}
          animate={nochargeShake ? { x: [-4, 4, -4, 4, 0] } : {}}
          transition={{ duration: 0.4 }}
          onClick={submit}
          className="rounded-md border border-ink-tealdim bg-ink-bg px-4 text-sm font-bold text-ink-teal transition hover:shadow-glow"
        >
          Craft
        </motion.button>
      </div>

      {/* Flying letters */}
      <AnimatePresence>
        {flyingLetters.map((fl) => (
          <motion.span
            key={fl.id}
            initial={{ opacity: 1, x: fl.startX, y: fl.startY, scale: 1 }}
            animate={{ opacity: 0, x: fl.startX + (Math.random() - 0.5) * 60, y: fl.startY - 80, scale: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="pointer-events-none fixed z-50 text-lg font-bold text-ink-teal"
          >
            {fl.char}
          </motion.span>
        ))}
      </AnimatePresence>

      {/* charge regen bar */}
      {quillCharge < GLOBAL.quillChargeMax && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded bg-ink-bg">
          <div className="h-full bg-ink-tealdim" style={{ width: `${regenPct}%` }} />
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[10px] text-ink-muted">
        <span>
          Daily Word: <span className="text-rarity-legend">{daily}</span> (+
          {GLOBAL.dailyWordBonus * 100}%)
        </span>
        <AnimatePresence>
          {chainLength > 1 && (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className={`font-bold ${
                tier >= 3 ? 'text-rarity-legend' : tier >= 2 ? 'text-rarity-rare' : 'text-ink-teal'
              }`}
            >
              combo ×{chainLength}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence mode="wait">
        {fb && (
          <motion.div
            key={lastCraft!.at}
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4 }}
            className={`mt-2 text-xs ${fb.cls}`}
          >
            {fb.text}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
